import type { FunctionComponent, InputHTMLAttributes, ReactNode, RefObject } from 'react';
import { useId, useRef } from 'react';

import { useModifiers } from '../../../styles/hooks/useModifiers';

import styles from './BaseInput.module.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'onSubmit' | 'label'> {
  /** Class to be added to the container */
  containerClass?: string;

  /** Error message translation key to be displayed below the input, takes priority over `supportingText` */
  errorMessage?: ReactNode;

  /** Ref to the input element */
  inputRef?: RefObject<HTMLInputElement>;

  /** Class to be added to the input */
  isErrorAnimated?: boolean;

  /** Label to be displayed above the input */
  label?: ReactNode;

  /** Prefix to be displayed before the input value */
  prefix?: ReactNode;

  /** Supporting text to be displayed below the input */
  supportingText?: ReactNode;
}

/**
 * # BaseInput
 *
 * A generic stylized input field, that can be customized with a prefix/postfix
 * Built on the base of: `<input />`
 * */
export const BaseInput: FunctionComponent<InputProps> = ({
  className = '',
  containerClass = '',
  disabled,
  errorMessage,
  id,
  inputRef,
  isErrorAnimated = false,
  label,
  onChange,
  placeholder,
  prefix,
  supportingText,
  value,
  ...rest
}) => {
  const generatedId = useId();
  const idToUse = id || generatedId;

  const localRef = useRef<HTMLInputElement>(null);
  const refToUse = inputRef || localRef;

  const mods = useModifiers(
    styles,
    'input__container',
    {
      disabled: disabled,
      error: !!errorMessage,
      animatedError: !!errorMessage && isErrorAnimated,
    },
    { withBaseClass: true }
  );

  const handleContainerClick = (): void => {
    inputRef?.current?.focus();
  };

  return (
    <div className={styles.input__wrapper}>
      {label ? (
        <label htmlFor={idToUse} className={styles.input__label}>
          {label}
        </label>
      ) : null}
      <div className={`${mods} ${containerClass}`} onClick={handleContainerClick}>
        {prefix ? <span className={styles.input__prefix}>{prefix}</span> : null}
        <input
          data-testid="input"
          className={`${styles.input} ${className}`}
          disabled={disabled}
          id={idToUse}
          onChange={onChange}
          placeholder={placeholder} // todo: add translation key, move out logic to chat footer component
          ref={refToUse}
          value={value}
          {...rest}
        />
      </div>
      {errorMessage ? (
        <div
          data-testid="input-error"
          className={`${styles.input__supportingText} ${styles.input__supportingText_error}`}
        >
          {errorMessage}
        </div>
      ) : null}
      {!errorMessage && supportingText ? (
        <div data-testid="input-supporting-text" className={styles.input__supportingText}>
          {supportingText}
        </div>
      ) : null}
    </div>
  );
};
